/// <amd-module name="Release/Landing/Hub" />
import * as React from "react";
import * as ReactDOM from 'react-dom';


import { IFavoritesService } from "Favorites/Common/FavoritesService";
import { Favorite } from "Favorites/Common/Contracts/Favorite";

import { localeFormat } from "VSS/Core/Util/String";
import { VssComponent, IVssComponentProperties, IVssComponentContext, ReactRootComponent } from "VSS/Platform/Layout";
import { HistoryServiceEventNames, IVssHistoryService, INavigationStateChangedEvent } from "VSS/Platform/History";
import { IVssFeatureService } from "VSS/Platform/Feature";
import { IObservableArray, ObservableArray, ObservableValue, IObservableArrayEventArgs } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";
import { ILegacyComponentProperties, LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { FormatComponent } from "VSS/Platform/Components/Format";

import { Page } from "VSSUI/Page";
import { Header, TitleSize } from "VSSUI/Header";
import { TabBar, TabSize, Tab } from "VSSUI/Tabs";
import { Link } from "VSSUI/Link";
import { IHeaderCommandBarItem, HeaderCommandBarWithFilter, HeaderCommandBar } from "VSSUI/HeaderCommandBar";
import { IFilterBarProps } from "VSSUI/FilterBar";
import { IFilter, Filter } from "VSSUI/Utilities/Filter";
import { ISplitterProps, SplitterElementPosition, Splitter } from "VSSUI/Splitter";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";

import { getTFSPageData } from "Tfs/Platform/Page";

import { IReleaseDefinitionPivotCommandContext } from "Release/Client/ExtensionContracts/IMenuCommandContracts";
import { DefinitionEnvironmentReference, ReleaseStatus } from "Release/Client/Contracts/Release";

import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";

import { userHasMemberClaim } from "Release/Helpers/Permission";

import { RightPanePivotBarKeys, MessageBarKeys, LeftPanePivotBarKeys, QueryParamKeys, ActiveReleaseColumnKeys, NamedStoreEvents } from "Release/Landing/Constants";
import { ReleasesPivot } from "Release/Landing/HubContent/ReleasesPivot";
import { DeploymentsPivot } from "HubContent/DeploymentsPivot";
import { AnalyticsPivot } from "HubContent/AnalyticsPivot";
import { LeftPanel } from "Release/Landing/LeftPanel/LeftPanel";
import { SelectionStore, IDefinitionSelection } from "Selection/SelectionStore";
import { MessageActions } from "HubMessageBar/MessageActions";
import { PipelinesActionCreator } from "LeftPanel/PipelinesActionCreator";
import { getHubPipelineCommands } from "HubCommands";
import { PipelinesStore, IReleasesPivotInfo } from "LeftPanel/PipelinesStore";
import { BranchesActionCreator } from "Release/Landing/HubContent/Filter/Branches/ActionCreator";
import { BranchesActions } from "./HubContent/Filter/Branches/Actions";
import { BranchesStore } from "Release/Landing/HubContent/Filter/Branches/Store";
import { getCurrentlyDeployedViewAction, Keys as ReleaseViewActionKeys, getColumnOptionHeaderCommands } from "Release/Landing/HubContent/Releases/ReleasesViewActions";
import { ActiveDefinitionReferenceType } from "Release/Landing/DataProviderContracts";
import { IReleaseHubServiceDataService } from "PageData";
import { DeploymentsFilterBar, setDeploymentsFilterState, getDeploymentsFilterBarState, getDefaultDeploymentsFilterOptions } from "HubContent/Filter/DeploymentsFilterBar";
import { ActiveReleasesFilterBar, setActiveReleasesFilterState, getReleasesFilterBarState, getDefaultReleasesFilterOptions } from "Release/Landing/HubContent/Filter/ActiveReleasesFilterBar";
import { IDeploymentsFilterState, IReleasesFilterState } from "Release/Landing/HubContent/Filter/FilterTypes";
import { isHostedOldUrl } from "./LandingUtils";
import { SelectionActions } from "Release/Landing/Selection/SelectionActions";
import * as Resources from "Release/Landing/Resources";
import { ShowAllReleasesSetting, ColumnViewOptionsSetting } from "Release/Landing/HubContent/Releases/ViewOptionsSetting";
import { markTimeToInteractive, PerfScenarios } from "Release/Helpers/Performance";
import { ActiveReleasesActions } from "Release/Landing/HubContent/Releases/ActiveReleasesActions";
import { IDeletedPipelinesUIService } from "./ExternalUIServices/IDeletedPipelinesUIService";
import { createSignalRHubIfNecessary, getReleasesSignalRHub, disconnectSignalRHub } from "Release/Landing/SignalR/LandingSignalRHub";

interface IState extends IReleasesPivotInfo {
    leftPanelSelectedPivot: string;
    releasesHubFilter: IFilter
    releasesFilterState: IReleasesFilterState;
    deploymentsFilterState: IDeploymentsFilterState;
    selectedFolderPath: string;
    isTabSwitchNavigation: boolean;
    rightPanelSelectedPivot: string;
    fixedPaneSize?: number;
}

interface IReleasesFilterUrlState {
    state: string | null | undefined;
    branch: string | null | undefined;
    deleted: string | null | undefined;
}


interface IDeploymentsFilterUrlState {
    branch: string | null | undefined;
    environmentId: string | null | undefined;
}

const filterToggled = new ObservableValue<boolean>(false);

export class ReleaseHub extends VssComponent<IVssComponentProperties, IState>{
    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        this._historyService = context.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        const historyState = this._historyService.getState();
        let isActionValid = true;
        let action = historyState["_a"];

        const definitionIdInUrl = historyState["definitionId"];
        const viewActionInUrl: string = historyState[LeftPanePivotBarKeys.NavigationKey];

        // If definition id is present in the url when the link is run from a browser or refreshed from a browser,
        // we will redirect to mine. Folders currently don't expand to any depth, so this is the best way to hold
        // the definition id
        const leftPanelPivotToSelect: string = (definitionIdInUrl
            || ![LeftPanePivotBarKeys.Active, LeftPanePivotBarKeys.All, LeftPanePivotBarKeys.Deleted]
                .some(v => v === viewActionInUrl))
            ? LeftPanePivotBarKeys.Active
            : viewActionInUrl;


        // Since we do not have contributed tabs, we will always have one of these 3 tabs as the action
        const validActions = [RightPanePivotBarKeys.Releases, RightPanePivotBarKeys.Analytics, RightPanePivotBarKeys.Deployments];
        if (leftPanelPivotToSelect !== LeftPanePivotBarKeys.Deleted && !validActions.some(k => k === action)) {
            isActionValid = false;

            // There is a chance that the action has been cased incorrectly. We treat all actions in the lower case. Convert to lower case if this happens
            // Otherwise, default to "releases"
            if (!!action && validActions.some(k => k === action.toLowerCase())) {
                action = action.toLowerCase();
            }
            else {
                action = RightPanePivotBarKeys.Releases;
            }

            historyState["_a"] = action;
        }

        const filterOptions = (!action || action === RightPanePivotBarKeys.Releases)
            ? getDefaultReleasesFilterOptions()
            : (action === RightPanePivotBarKeys.Deployments)
                ? getDefaultDeploymentsFilterOptions()
                : {}

        const releasesHubFilter = new Filter(filterOptions);

        this._selectionStore = StoreManager.GetStore<SelectionStore>(SelectionStore);
        this._pipelinesStore = StoreManager.GetStore<PipelinesStore>(PipelinesStore);

        this._messageActions = ActionsHubManager.GetActionsHub<MessageActions>(MessageActions);
        this._activeReleasesActions = ActionsHubManager.GetActionsHub<ActiveReleasesActions>(ActiveReleasesActions);

        this._pipelineActionCreator = ActionCreatorManager.GetActionCreator<PipelinesActionCreator>(PipelinesActionCreator);

        this._historyService.subscribe(this._onHistoryPopped, HistoryServiceEventNames.statePopped);

        this._favoritesService = context.pageContext.getService<IFavoritesService>("IFavoritesService");
        this._favoritesService.subscribe(this._onFavoriteToggle, "favoriteAdded");
        this._favoritesService.subscribe(this._onFavoriteToggle, "favoriteRemoved");
        this._favoritesService.subscribe(this._onFavoriteError, "favoriteError");

        if (viewActionInUrl !== leftPanelPivotToSelect || !isActionValid) {
            historyState[LeftPanePivotBarKeys.NavigationKey] = leftPanelPivotToSelect;
            this._historyService.replaceState(historyState);
        }
        this._branchesActionCreator = ActionCreatorManager.GetActionCreator<BranchesActionCreator>(BranchesActionCreator);
        this._branchesActions = ActionsHubManager.GetActionsHub<BranchesActions>(BranchesActions);

        this._branchesStore = StoreManager.GetStore<BranchesStore>(BranchesStore);

        let releasesFilterState: IReleasesFilterState | undefined;
        let deploymentsFilterState: IDeploymentsFilterState | undefined;

        if (!action || action === RightPanePivotBarKeys.Releases) {
            releasesFilterState = this._getReleasesFilterStateFromUrl();
            if (releasesFilterState.branch || releasesFilterState.createdBy || releasesFilterState.isDeleted || releasesFilterState.status !== ReleaseStatus.Undefined) {
                filterToggled.value = true;
            }

            releasesHubFilter.setState(getReleasesFilterBarState(releasesFilterState), true);
        }
        else {
            deploymentsFilterState = this._getDeploymentsFilterStateFromUrl();
            if (deploymentsFilterState.branch || deploymentsFilterState.createdBy || deploymentsFilterState.environmentId > 0) {
                filterToggled.value = true;
            }

            releasesHubFilter.setState(getDeploymentsFilterBarState(deploymentsFilterState), true);
        }

        filterToggled.subscribe(this._setStateOnFilterToggle);

        const releasesHubDataService = context.pageContext.getService<IReleaseHubServiceDataService>("IReleaseHubServiceDataService");
        const selectedPipeline = releasesHubDataService.getSelectedDefinition();
        const selectedFolderPath = releasesHubDataService.getSelectedFolderPath();
        if (selectedPipeline) {
            this._historyService.replaceState({ ...historyState, "definitionId": selectedPipeline.id.toString() });
            ActionsHubManager.GetActionsHub<SelectionActions>(SelectionActions).selectInitialDefinition.invoke({ folderId: "", id: selectedPipeline.id, noSelection: false });
            if (filterToggled.value === true) {
                this._branchesActionCreator.getBranches(this.context.pageContext, selectedPipeline.id);
            }
        }
        else if (selectedFolderPath) {
            ActionsHubManager.GetActionsHub<SelectionActions>(SelectionActions).selectInitialDefinition.invoke({ folderId: selectedFolderPath, id: -1, noSelection: false });
        }

        const selection = this._selectionStore.getState();
        const computedState = this._getStateFromSelection(selection, releasesFilterState, deploymentsFilterState);
        if (selectedPipeline) {
            if (!computedState.releaseDefinitionName) {
                computedState.releaseDefinitionName = selectedPipeline.name;
            }

            if (!computedState.folderPath) {
                computedState.folderPath = selectedPipeline.path;
            }
        }
        else if (selectedFolderPath) {
            if (!computedState.selectedFolderPath) {
                computedState.selectedFolderPath = selectedFolderPath;
            }
        }

        this.state = {
            ...computedState, ...{
                leftPanelSelectedPivot: leftPanelPivotToSelect,
                fixedPaneSize: this._initialFixedSize,
                releasesHubFilter: releasesHubFilter,
                rightPanelSelectedPivot: action
            }
        };

        this._selectionStore.addChangedListener(this._onSelectionChange);

        this._pipelinesStore.addListener(NamedStoreEvents.Pileline_Renamed_Moved_Event, this._onRenameOrMovePipeline);

        this._setColumnOptionsViewState(selection.id);
        this._setCurrentlyDeployedViewState(selection.id);

        this._createSignalRHub();
    }

    public componentDidMount(): void {
        setTimeout(() => {
            const dummyContainer = document.createElement("div");
            document.body.appendChild(dummyContainer);
            let component = (<ReactRootComponent
                pageContext={this.context.pageContext}>
                <LegacyComponent
                    modules={["PipelineWorkflow/Scripts/Shared/LegacyLWP/PrefetchPipelineWorkFlow"]}
                    dependentContributionIds={["ms.vss-releaseManagement-web.release-service-data"]}
                    wrappedType={"prefetch-progressview-editor"}
                    dependencies={[
                        "ms.vss-web.legacy-vss-ui-content",
                        "ms.vss-work-web.common-content",
                        "ms.vss-work-web.wit-ui-work-item-grid",
                        "ms.vss-distributedtask-web.dt-process-logs",
                        "ms.vss-distributedtask-web.common-library"]} />
            </ReactRootComponent >);
            ReactDOM.render(component, dummyContainer);
        }, 2000);
    }

    public componentWillUnmount(): void {
        this._pipelinesStore.removeListener(NamedStoreEvents.Pileline_Renamed_Moved_Event, this._onRenameOrMovePipeline);
        this._selectionStore.removeChangedListener(this._onSelectionChange);
        this._currentlyDeployedState && this._currentlyDeployedState.unsubscribe(this._onCurrentlyDeployedChange);
        this._showDescriptionColumn && this._showDescriptionColumn.unsubscribe(this._onColumnOptionsChanged);
        this._showTagsColumn && this._showTagsColumn.unsubscribe(this._onColumnOptionsChanged);
        this._historyService.unsubscribe(this._onHistoryPopped, HistoryServiceEventNames.statePopped);
        this._favoritesService.unsubscribe(this._onFavoriteToggle, "favoriteAdded");
        this._favoritesService.unsubscribe(this._onFavoriteToggle, "favoriteRemoved");
        this._favoritesService.unsubscribe(this._onFavoriteError, "favoriteError");
        filterToggled.unsubscribe(this._setStateOnFilterToggle);
    }

    public render(): React.ReactNode {

        const leftPanelKey = this.state.leftPanelSelectedPivot;

        const leftPanel = this._onRenderLeftPanel();

        let state = this.state;
        const selectedPath = state.selectedFolderPath;
        const title = state.releaseDefinitionName
            || (selectedPath
                && selectedPath === "\\" ? Resources.AllDefinitions : selectedPath.substring(selectedPath.lastIndexOf("\\") + 1))
            || Resources.NoPipelineSelected;
        let pageContent: React.ReactNode = null;
        if (leftPanelKey === LeftPanePivotBarKeys.Deleted) {
            pageContent = this.context.pageContext.getService<IDeletedPipelinesUIService>("IDeletedPipelinesUIService").getRightPaneUI(this._handleUndelete, this._undeleteFailed, this._markTTI);
        }
        else {
            let filterBar;
            if (filterToggled.value) {
                const renderFilter = this._getOnRenderFilterBar();
                if (renderFilter) {
                    filterBar = renderFilter();
                }
            }

            pageContent = (<Page
                className={"flex-grow release-landing-page"}
            >
                <Header
                    title={title}
                    titleSize={TitleSize.Large}
                    commandBarItems={this._getHeaderCommandItems()}
                />
                
                <TabBar
                    className={"release-right-tab-bar"}
                    selectedTabId={this.state.rightPanelSelectedPivot || RightPanePivotBarKeys.Releases}
                    onSelectedTabChanged={(selectedTab: string) => this._onRightPanelPivotClicked(selectedTab)}
                    tabSize={TabSize.Tall}
                    renderAdditionalContent={this._renderTabBarCommands}
                >

                    <Tab
                        name={Resources.ReleasesPivotBarItemName}
                        id={RightPanePivotBarKeys.Releases}
                    />

                    { // Do not show deployments pivot if folder is selected in left panel
                        !this.state.selectedFolderPath &&
                        <Tab
                            name={Resources.DeploymentsPivotBarItemName}
                            id={RightPanePivotBarKeys.Deployments}
                        />
                    }

                    { // Do not show Analytics pivot if folder is selected in left panel
                        !this.state.selectedFolderPath &&
                        userHasMemberClaim(this.context.pageContext) &&
                        <Tab
                            name={Resources.AnalyticsPivotBarItemName}
                            id={RightPanePivotBarKeys.Analytics}
                        />
                    }
                </TabBar>

                {filterBar}

                <div className="right-tab-content flex-column flex-grow">
                    {this._getHubTabContent()}
                </div>

            </Page>);
        }

        return (
            <Splitter
                fixedElement={SplitterElementPosition.Near}
                initialFixedSize={this._initialFixedSize}
                onRenderFarElement={() => pageContent as JSX.Element}
                onRenderNearElement={() => leftPanel}
            />);
    }

    private _onRenderLeftPanel = (): JSX.Element => {
        return (<LeftPanel
            selectedPivot={this.state.leftPanelSelectedPivot}
            onPivotChange={this._onLeftPanelPivotChange}
        />);
    }

    private _onLeftPanelPivotChange = (selectedPivotKey: string): void => {
        const historyState = this._historyService.getState();
        historyState[LeftPanePivotBarKeys.NavigationKey] = selectedPivotKey;
        this._historyService.replaceState(historyState);
        this.setState({
            leftPanelSelectedPivot: selectedPivotKey
        });
    }

    private _getOnRenderFilterBar(): (() => React.ReactElement<IFilterBarProps>) | undefined {
        if (this.state.releaseDefinitionId > 0 && this.state.rightPanelSelectedPivot === RightPanePivotBarKeys.Deployments) {
            return this._onRenderFilterBarForDeployments;
        }
        else {
            let isCurrentlyDeployed = this._currentlyDeployedState.value === ReleaseViewActionKeys.CurrentlyDeployed;

            if ((this.state.releaseDefinitionId > 0 || !!this.state.selectedFolderPath) && !isCurrentlyDeployed && (!this.state.rightPanelSelectedPivot || this.state.rightPanelSelectedPivot === RightPanePivotBarKeys.Releases)) {
                return this._onRenderFilterBarForReleases;
            }
        }
    }

    private _onRenderFilterBarForReleases = (): React.ReactElement<IFilterBarProps> => {
        return <ActiveReleasesFilterBar
            filter={this.state.releasesHubFilter} // We will use the hub filter for both releases and deployments
            definitionId={this.state.releaseDefinitionId}
            pageContext={this.context.pageContext}
            selectedFolderPath={this.state.selectedFolderPath}
            onFiltersUpdated={(filterState: IReleasesFilterState) => {
                const isCurrentlyDeployed = this._currentlyDeployedState.value === ReleaseViewActionKeys.CurrentlyDeployed;
                filterState.currentlyDeployed = isCurrentlyDeployed;
                this.setState({ releasesFilterState: filterState });
                this._updateHistoryWithReleasesFilter(filterState);
            }}
        />;
    }

    private _onRenderFilterBarForDeployments = (): React.ReactElement<IFilterBarProps> => {
        const environmentsMap: { [key: number]: string } = {};
        this.state.environments.forEach((env) => {
            environmentsMap[env.definitionEnvironmentId] = env.definitionEnvironmentName;
        });

        return <DeploymentsFilterBar
            filter={this.state.releasesHubFilter} // We will use the hub filter for both releases and deployments
            definitionId={this.state.releaseDefinitionId}
            environmentIdNameMap={environmentsMap}
            pageContext={this.context.pageContext}
            onFiltersUpdated={(filterState: IDeploymentsFilterState) => {
                this.setState({ deploymentsFilterState: filterState });
                this._updateHistoryWithDeploymentsFilter(filterState);
            }}
        />;
    }

    private _getHubTabContent(): React.ReactNode {
        const state = this.state;
        const selectedPath = state.selectedFolderPath;
        switch (this.state.rightPanelSelectedPivot) {
            case RightPanePivotBarKeys.Deployments: return (<DeploymentsPivot
                definitionId={state.releaseDefinitionId}
                definitionName={state.releaseDefinitionName}
                definitionFolderPath={state.folderPath}
                selectedFolderPath={selectedPath}
                definitionReferenceType={state.definitionType}
                isSearchedItem={state.definitionType === ActiveDefinitionReferenceType.SearchedResult}
                isTabSwitchNavigation={state.isTabSwitchNavigation}
                filterState={this.state.deploymentsFilterState}
                markTTICallback={this._markTTI}
            />);

            case RightPanePivotBarKeys.Analytics: return (
                <div className={"analytics-tab"}>
                    <AnalyticsPivot
                        pipelineId={state.releaseDefinitionId}
                        selectedFolderPath={state.selectedFolderPath}
                        isTabSwitchNavigation={state.isTabSwitchNavigation}
                        markTTICallback={this._markTTI}
                    />
                </div>);

            default: return (<ReleasesPivot
                definitionId={state.releaseDefinitionId}
                definitionName={state.releaseDefinitionName}
                definitionFolderPath={state.folderPath}
                selectedFolderPath={selectedPath}
                definitionReferenceType={state.definitionType}
                isSearchedItem={state.definitionType === ActiveDefinitionReferenceType.SearchedResult}
                isTabSwitchNavigation={state.isTabSwitchNavigation}
                filterState={state.releasesFilterState}
                fixedPaneSize={this.state.fixedPaneSize || 320}
                markTTICallback={this._markTTI}
            />);

        }
    }

    private _renderTabBarCommands = (): JSX.Element | undefined => {
        const selectedPivot = this.state.rightPanelSelectedPivot;
        if (selectedPivot === RightPanePivotBarKeys.Deployments) {
            return <HeaderCommandBarWithFilter filter={this.state.releasesHubFilter} filterToggled={filterToggled} items={[]} />
        }
        else if (!selectedPivot || selectedPivot === RightPanePivotBarKeys.Releases) {

            const items = this._getTabBarCommands();
            if (this.state.releasesFilterState.currentlyDeployed) {
                return <HeaderCommandBar items={items} />
            }
            else {
                return <HeaderCommandBarWithFilter filter={this.state.releasesHubFilter} filterToggled={filterToggled} items={items} />;
            }
        }
    }

    private _getHeaderCommandItems(): IObservableArray<IHeaderCommandBarItem> {
        let state = this.state;
        if (state.releaseDefinitionId <= 0) {
            return new ObservableArray<IHeaderCommandBarItem>();
        }

        const successCallback = (message: React.ReactNode, isDeleted?: boolean) => {
            if (isDeleted) {
                const removedPipelinePath = this._pipelinesStore.getDefinitionPath(this.state.releaseDefinitionId);
                this._pipelineActionCreator.removePipeline(this.state.releaseDefinitionId, removedPipelinePath || "\\");
            }

            this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubSuccess, value: message });
        };

        const commandContext = {
            pageContext: this.context.pageContext,
            pipelineId: state.releaseDefinitionId,
            pipelineName: state.releaseDefinitionName,
            isFolder: false,
            folderPath: state.folderPath,
            successCallback: successCallback,
            errorCallback: (message: React.ReactNode) => this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubError, value: message })
        } as IReleaseDefinitionPivotCommandContext;

        const featureService: IVssFeatureService = this.context.pageContext.getService<IVssFeatureService>("IVssFeatureService");
        const isClassicPipelineCreationdDisabled = featureService.isFeatureEnabled("ms.vss-build-web.disable-classic-release-pipeline-creation", false)

        const commands = getHubPipelineCommands(commandContext, isClassicPipelineCreationdDisabled);

        const contributedMenuItems = getMenuItems(this.context.pageContext,
            "ms.vss-releaseManagement-web.release-definition-explorer-context-menu",
            {
                definition: {
                    id: this.state.releaseDefinitionId,
                    name: this.state.releaseDefinitionName,
                    path: this.state.folderPath
                }
            });

        contributedMenuItems.value.forEach(menuItem => {
            const headerMenuItem = (menuItem as IHeaderCommandBarItem);
            headerMenuItem.important = headerMenuItem.important === true;

            commands.push(menuItem);
        });

        contributedMenuItems.subscribe((update) => {
            if (update.addedItems) {
                update.addedItems.forEach(menuItem => commands.push(menuItem));
            }
        });

        return commands;
    }

    private _getTabBarCommands(): IHeaderCommandBarItem[] {
        const viewActionOptions = !this.state.selectedFolderPath
            ? [getCurrentlyDeployedViewAction(this._currentlyDeployedState)]
            : [];

        viewActionOptions.push(...getColumnOptionHeaderCommands(this._showDescriptionColumn, this._showTagsColumn));
        viewActionOptions.forEach(o => o.subtle = true);
        return viewActionOptions;
    }

    private _setStateOnFilterToggle = () => {
        const branchesState = this._branchesStore.getState();
        if (!branchesState.branches || branchesState.definitionId !== this.state.releaseDefinitionId) {
            this._branchesActionCreator.getBranches(this.context.pageContext, this.state.releaseDefinitionId);
        }

        this.setState({})
    };

    private _setCurrentlyDeployedViewState(definitionId: number): void {
        let value = ReleaseViewActionKeys.AllReleases;
        const settingsValue = ShowAllReleasesSetting.getValue(this.context.pageContext, definitionId);

        if (settingsValue === undefined) {
            // If the setting has never been set, then choose below
            value = ReleaseViewActionKeys.AllReleases;
        }
        else {
            value = settingsValue ? ReleaseViewActionKeys.AllReleases : ReleaseViewActionKeys.CurrentlyDeployed;
        }

        // Update state's currently deployed property without setState so that re-rendering is not triggered.
        this.state.releasesFilterState.currentlyDeployed = value === ReleaseViewActionKeys.CurrentlyDeployed;

        if (this._currentlyDeployedState) {
            this._currentlyDeployedState.unsubscribe(this._onCurrentlyDeployedChange);
        }

        this._currentlyDeployedState = new ObservableValue<string>(value);
        this._currentlyDeployedState.subscribe(this._onCurrentlyDeployedChange);
    }

    private _setColumnOptionsViewState(itemKey: number | string): void {
        let showDescriptionColumn: boolean = false;
        let showTagsColumn: boolean = false;
        const columnsToShow = ColumnViewOptionsSetting.getValue(this.context.pageContext, itemKey);

        if (columnsToShow && columnsToShow.length > 0) {
            showDescriptionColumn = columnsToShow.findIndex((key) => key === ActiveReleaseColumnKeys.ActiveReleaseDescriptionKey) > -1;
            showTagsColumn = columnsToShow.findIndex((key) => key === ActiveReleaseColumnKeys.ActiveReleaseTagsKey) > -1;
        }

        if (this._showDescriptionColumn) {
            this._showDescriptionColumn.unsubscribe(this._onColumnOptionsChanged);
        }

        this._showDescriptionColumn = new ObservableValue(showDescriptionColumn);
        this._showDescriptionColumn.subscribe(this._onColumnOptionsChanged);

        if (this._showTagsColumn) {
            this._showTagsColumn.unsubscribe(this._onColumnOptionsChanged);
        }

        this._showTagsColumn = new ObservableValue(showTagsColumn)
        this._showTagsColumn.subscribe(this._onColumnOptionsChanged);
    }

    private _onCurrentlyDeployedChange = () => {
        const showCurrentlyDeployed = this._currentlyDeployedState.value === ReleaseViewActionKeys.CurrentlyDeployed;

        const selectedDefinitionId = this.state.releaseDefinitionId;
        if (selectedDefinitionId > 0) {
            // If viewOptionsValues has this key, it implies that there is a change in the the particular view option
            ShowAllReleasesSetting.setValue(this.context.pageContext, selectedDefinitionId, !showCurrentlyDeployed);
        }

        let filterState = { ...this.state.releasesFilterState };
        filterState.currentlyDeployed = showCurrentlyDeployed;

        this.setState({ releasesFilterState: filterState });
    }

    private _onColumnOptionsChanged = (): void => {
        const showDescriptionColumn = this._showDescriptionColumn && this._showDescriptionColumn.value;
        const showTagsColumn = this._showTagsColumn && this._showTagsColumn.value;
        const selectedDefinitionId = this.state.releaseDefinitionId;
        const visibleColumns: string[] = [];
        if (showDescriptionColumn) {
            visibleColumns.push(ActiveReleaseColumnKeys.ActiveReleaseDescriptionKey);
        }

        if (showTagsColumn) {
            visibleColumns.push(ActiveReleaseColumnKeys.ActiveReleaseTagsKey);
        }

        this._activeReleasesActions.setColumnOptions.invoke({ definitionId: selectedDefinitionId, folderPath: this.state.selectedFolderPath, visibleColumns: visibleColumns });

        ColumnViewOptionsSetting.setValue(this.context.pageContext, selectedDefinitionId > 0 ? selectedDefinitionId : this.state.selectedFolderPath, visibleColumns);
        this.setState({});
    }

    private _onSelectionChange = (): void => {
        const selection = this._selectionStore.getState();

        if (selection.noSelection) {
            // Handle no selection (this would occur immediately after search)

            this._updateHistoryState(selection);
            this.setState({
                showSelectPipeline: true,
                releaseDefinitionName: "",
                releaseDefinitionId: selection.id
            });
        }
        else if (!!selection.folderId && this.state.selectedFolderPath !== selection.folderId) {
            // If the selected folder Id is empty, it means that a release definition is selected, This section only deals with cases when a folder is selected

            this._setColumnOptionsViewState(selection.folderId);
            this._updateHistoryState(selection);
            this._clearAllUrlFilterKeys();

            // Clear out all filter values. The following steps need to be backed by setting default values in this.state.filterState which is done in _getStateFromSelection
            // TODO - improve this code, so that dependent pieces are bound together in a single function
            let filterState = this.state.releasesHubFilter.getState();
            filterState = setActiveReleasesFilterState({});
            filterState = setDeploymentsFilterState(filterState);
            this.state.releasesHubFilter.setState(filterState, true);

            const state = this._getStateFromSelection(selection);
            this.setState(state);
        }
        else if (this.state.releaseDefinitionId !== selection.id) {
            // This is the case where the definitionId has changed. If selection.id > 0, then a definition has been selected, otherwise, it is probably a folder

            if (selection.id > 0) {
                // Computing currently deployed for the current definition. This will be stored in this.state.filterState.
                // We'll use this value to store it in the state before we invoke setState
                this._setColumnOptionsViewState(selection.id);
                this._setCurrentlyDeployedViewState(selection.id);
                this._updateHistoryState(selection);

                setTimeout(() => this._branchesActions.updateBranches.invoke({ branches: undefined, definitionId: selection.id }), 0); // timeout to prevent this action from raised within the selection changed action

                if (filterToggled.value === true) {
                    this._branchesActionCreator.getBranches(this.context.pageContext, selection.id);
                }

                // Get selection state with filter states from the url if this is the first selection
                let state: IState

                let filterState = this.state.releasesHubFilter.getState();

                // Clear out all filter values. The following steps need to be backed by setting default values in this.state.filterState which is done in _getStateFromSelection
                // TODO - improve this code, so that dependent pieces are bound together in a single function
                filterState = setActiveReleasesFilterState({});
                filterState = setDeploymentsFilterState(filterState);
                this.state.releasesHubFilter.setState(filterState, true);

                this._clearAllUrlFilterKeys();
                state = this._getStateFromSelection(selection);

                state.releasesFilterState.currentlyDeployed = this.state.releasesFilterState.currentlyDeployed;
                this.setState(state);
            }
        }
        else if (this.state.folderPath !== selection.folderId) {
            this.setState(this._getStateFromSelection(selection, this.state.releasesFilterState, this.state.deploymentsFilterState));
        }
    }

    private _onRenameOrMovePipeline = () => {
        const selection = this._selectionStore.getState();
        const releasesPivotInfo = this._pipelinesStore.getHubStateFromSelection(selection);

        this.setState({ releaseDefinitionName: releasesPivotInfo.releaseDefinitionName, folderPath: releasesPivotInfo.folderPath });
    }

    private _onHistoryPopped = (popStateEvent: INavigationStateChangedEvent): void => {
        const { newState } = popStateEvent;
        if (newState && newState.state) {
            const selectedDefinitionIdString = newState.state[QueryParamKeys.DefinitionIdKey];
            const path = newState.state[QueryParamKeys.PathKey];
            const selectionActions = ActionsHubManager.GetActionsHub<SelectionActions>(SelectionActions);
            // Do not update history state on back/forward navigation as the url is already taken care of
            const selectedDefinitionId = selectedDefinitionIdString ? parseInt(selectedDefinitionIdString, 10) : -1;
            selectionActions.selectDefinition.invoke({
                id: selectedDefinitionId,
                folderId: path || "",
                noSelection: (selectedDefinitionId <= 0 && !path),
                updateHistoryState: false
            });
        }
    }

    private _onFavoriteToggle = (favorite: Favorite): void => {
        // If currently selected RD is favorited/unfavorited, then re -render in order to update the menu items
        if (parseInt(favorite.artifactId) === this.state.releaseDefinitionId) {
            this.setState({ ...this.state });
        }
    }

    private _onFavoriteError = (error: Error): void => {
        const message = error.message || error;
        this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubError, value: message });
    }

    private _onRightPanelPivotClicked = (pivotKey: string) => {
        this._handleUrlFilterKeys(pivotKey);

        const historyState = this._historyService.getState();
        historyState["_a"] = pivotKey;
        this._historyService.replaceState(historyState);

        this.setState({
            isTabSwitchNavigation: true,
            rightPanelSelectedPivot: pivotKey
        });
    }

    private _handleUndelete = (pipelineId: number, pipelineName: string) => {
        this._pipelineActionCreator.handleUndeletePipeline(this.context.pageContext);
        this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubSuccess, value: localeFormat(Resources.PipelineUndeletedMessage, pipelineName) });
    }

    private _undeleteFailed = (pipelineId: number, pipelineName: string, error: any) => {
        const message = error.message || error;
        this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubError, value: message });
    }

    private _createSignalRHub(): void {
        if (isHostedOldUrl(this.context.pageContext)) {
            const message = (<FormatComponent format={Resources.NoLivenessOldUrl}>
                <Link href={"https://aka.ms/new-domain"}
                    rel={"noopener noreferrer"}
                    target={"_blank"}>
                    {Resources.LearnMore}
                </Link>
            </FormatComponent>);

            this._messageActions.setMessage.invoke({ key: MessageBarKeys.SignalR, value: message });
            return;
        }

        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        let contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        let data = contributionService.getData<any>("ms.vss-releaseManagement-web.rm-signalr-data-provider-new");

        const vssPageData = vssPageService.getData();

        const tfsPageData = getTFSPageData(this.context.pageContext);

        if (vssPageData && tfsPageData && tfsPageData.project && data && data.connectionUrl) {
            const hostId = vssPageData.hostId;
            const isHosted = vssPageData.isHosted;

            const disconnectedMessage = Resources.SignalRDisconnectedMessage;
            const disconnectedCallback = (message: string) => this._messageActions.setMessage.invoke({ key: MessageBarKeys.SignalR, value: message });

            createSignalRHubIfNecessary({
                hostInformation: {
                    hostId: hostId,
                    hostPath: data.connectionUrl.split("_apis")[0],
                    isHosted: isHosted,
                    projectId: tfsPageData.project.id
                },
                projectId: tfsPageData.project.id,
                pageContext: this.context.pageContext,
                onDisconnected: () => disconnectedCallback(disconnectedMessage),
                onReconnected: () => disconnectedCallback(""),
                onUserInactive: () => {
                    const signalRHub = getReleasesSignalRHub();
                    if (signalRHub) {
                        disconnectedCallback(disconnectedMessage);
                        disconnectSignalRHub();
                    }
                }
            });
        }
    }

    private _updateHistoryState(selection: IDefinitionSelection): void {
        const definitionIdKey = QueryParamKeys.DefinitionIdKey;
        const pathKey = QueryParamKeys.PathKey;
        if (selection.updateHistoryState !== false) {
            const state = this._historyService.getState();

            // Check and add definition id or delete it
            if (selection.id > 0) {
                state[definitionIdKey] = selection.id.toString(10);
            }
            else {
                delete state[definitionIdKey];
            }

            // Check and add folder path or delete it
            if (selection.folderId) {
                state[pathKey] = selection.folderId;
            }
            else {
                delete state[pathKey];
            }

            if (selection.noSelection) {
                // If showing the no pipeline selected view then remove definition Id and folder path from url
                delete state[definitionIdKey];
                delete state[pathKey];
            }

            if ((this.state.releaseDefinitionId <= 0 && !this.state.selectedFolderPath) || selection.noSelection) {
                // If there is no definition selected, then don't push
                this._historyService.replaceState(state);
            }
            else {
                this._historyService.pushState(state);
            }
        }
    }

    // Revisit the placement of this when we experiment with contributed pivots
    // This could sit inside the pivot implementation
    private _updateHistoryWithReleasesFilter(filterState: IReleasesFilterState) {

        let urlState: IReleasesFilterUrlState = {} as IReleasesFilterUrlState;
        if (filterState.currentlyDeployed) {
            urlState = {
                branch: null,
                state: null,
                deleted: null
            }
        }
        else {
            if (filterState.status !== ReleaseStatus.Undefined
                && filterState.status !== (ReleaseStatus.Abandoned | ReleaseStatus.Active | ReleaseStatus.Draft)) {
                urlState.state = filterState.status.toString();
            }
            else {
                urlState.state = null;
            }

            urlState.branch = filterState.branch ? filterState.branch : null;
            urlState.deleted = filterState.isDeleted ? "true" : null;
        }

        let historyState = this._historyService.getState();
        for (const key in urlState) {
            if (urlState.hasOwnProperty(key)) {
                historyState[key] = (urlState as any)[key];
            }
        }

        this._historyService.replaceState(historyState);
    }

    // Revisit the placement of this when we experiment with contributed pivots
    // This could sit inside the pivot implementation
    private _updateHistoryWithDeploymentsFilter(filterState: IDeploymentsFilterState) {

        let urlState: IDeploymentsFilterUrlState = {
            branch: filterState.branch ? filterState.branch : null,
            environmentId: filterState.environmentId ? filterState.environmentId.toString() : null
        }

        let historyState = this._historyService.getState();
        for (const key in urlState) {
            if (urlState.hasOwnProperty(key)) {
                historyState[key] = (urlState as any)[key];
            }
        }

        this._historyService.replaceState(historyState);
    }

    // Revisit the placement of this when we experiment with contributed pivots
    // This could sit inside the pivot implementation
    private _handleUrlFilterKeys(selectedPivot: string): void {
        this._clearAllUrlFilterKeys();

        if (selectedPivot === RightPanePivotBarKeys.Deployments) {
            this._updateHistoryWithDeploymentsFilter(this.state.deploymentsFilterState);
        }
        else if (selectedPivot === RightPanePivotBarKeys.Releases) {
            this._updateHistoryWithReleasesFilter(this.state.releasesFilterState);
        }
    }

    private _clearAllUrlFilterKeys(): void {
        const urlState: any = this._historyService.getState();
        // Remove all filter related parameters
        urlState.branch = null;
        urlState.createdBy = null;
        urlState.state = null;
        urlState.deleted = null;
        urlState.environmentId = null;

        this._historyService.replaceState(urlState);
    }

    private _getReleasesFilterStateFromUrl(): IReleasesFilterState {
        const urlState: IReleasesFilterUrlState = this._historyService.getState() as any;
        return {
            branch: urlState.branch || undefined,
            currentlyDeployed: false,
            createdBy: "",
            isDeleted: urlState.deleted === "true" ? true : false,
            searchText: "",
            status: (urlState.state && parseInt(urlState.state) as ReleaseStatus) || ReleaseStatus.Undefined,
            tags: []
        };
    }

    private _getDeploymentsFilterStateFromUrl(): IDeploymentsFilterState {
        const urlState: IDeploymentsFilterUrlState = this._historyService.getState() as any;
        return {
            branch: urlState.branch || undefined,
            createdBy: "",
            environmentId: (urlState.environmentId && parseInt(urlState.environmentId)) || 0
        };
    }

    private _getStateFromSelection(
        selection: IDefinitionSelection,
        releasesFilterState: IReleasesFilterState = ActiveReleasesFilterBar.getDefaultFilterState(),
        deploymentsFilterState: IDeploymentsFilterState = DeploymentsFilterBar.getDefaultFilterState()): IState {
        const releasesPivotInfo = this._pipelinesStore.getHubStateFromSelection(selection);
        return {
            leftPanelSelectedPivot: this.state && this.state.leftPanelSelectedPivot || "",
            releasesHubFilter: this.state && this.state.releasesHubFilter,
            releaseDefinitionId: releasesPivotInfo.releaseDefinitionId,
            releaseDefinitionName: releasesPivotInfo.releaseDefinitionName,
            showSelectPipeline: selection.noSelection,
            definitionType: releasesPivotInfo.definitionType,
            environments: releasesPivotInfo.environments,
            folderPath: releasesPivotInfo.folderPath,
            selectedFolderPath: selection.folderId || "",
            releasesFilterState: releasesFilterState,
            deploymentsFilterState: deploymentsFilterState,
            isTabSwitchNavigation: false,
            rightPanelSelectedPivot: this.state && this.state.rightPanelSelectedPivot || ""
        };
    }

    private _markTTI = () => {
        if (!this._pageLoadMarked) {
            markTimeToInteractive(this.context.pageContext, PerfScenarios.ReleasesHub);
            this._pageLoadMarked = true;
        }
    }

    private _currentlyDeployedState: ObservableValue<string>;
    private _showDescriptionColumn: ObservableValue<boolean>;
    private _showTagsColumn: ObservableValue<boolean>;

    private _selectionStore: SelectionStore;
    private _pipelinesStore: PipelinesStore;
    private _messageActions: MessageActions;
    private _pipelineActionCreator: PipelinesActionCreator;
    private _branchesActionCreator: BranchesActionCreator;
    private _branchesActions: BranchesActions;
    private _branchesStore: BranchesStore;
    private _historyService: IVssHistoryService;
    private _favoritesService: IFavoritesService;
    private _initialFixedSize: number = 320;
    private _pageLoadMarked: boolean = false;
    private _activeReleasesActions: ActiveReleasesActions;
}